import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { useAuth } from '../../context/auth-context';
import { useIncomingCall }
    from '../../modules/call/hooks/useIncomingCall';
import { IncomingCallModal }
    from '../../modules/call/components/IncomingCallModal';
import { ChangePasswordModal }
    from './ChangePasswordModal';
import { formatTodayHeader } from '../../core/utils/dateUtils';
import { usePermissions } from '../../core/hooks/usePermissions';
import tiecheLogo from '../../assets/tieche-logo.png';
import avtARLogo from '../../assets/avtAR_logo.png';
import './AppLayout.css';

const AppLayout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const userMenuRef = useRef<Menu | null>(null);
    const { user, logout } = useAuth();
    const { canView } = usePermissions();
    const userName = user?.name ?? 'User';
    const userEmail = user?.email ?? '';

    const [showChangePassword, setShowChangePassword] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>(
        'Notification' in window ? Notification.permission : 'unsupported'
    );
    const [showNotifBanner, setShowNotifBanner] = useState(true);

    // Request notification permission once at login so incoming calls
    // can show OS notifications even when the user is on another tab.
    useEffect(() => {
        if (!('Notification' in window)) return;
        if (Notification.permission === 'default') {
            void Notification.requestPermission().then((perm) => {
                setNotifPermission(perm);
            });
        }
    }, []);

    // ── Nav items ──────────────────────────────────────────────
    const navItems = [
        { id: 'devices', label: 'DEVICES', path: '/devices' },
        ...(canView('users') ? [
            { id: 'users', label: 'USER MANAGEMENT', path: '/users' },
        ] : []),
        ...(canView('roles') ? [
            { id: 'roles', label: 'ROLE MANAGEMENT', path: '/roles' },
        ] : []),
    ];

    // ── User dropdown ──────────────────────────────────────────
    const userMenuItems: MenuItem[] = [
        {
            template: () => (
                <div className="px-2 py-1 border-bottom surface-50">
                    <div className="font-medium text-base">{userName}</div>
                    <div className="text-sm text-color-secondary">{userEmail}</div>
                </div>
            ),
        },
        { separator: true },
        {
            id: 'change-password',
            label: 'Change Password',
            icon: <i className="pi pi-lock mr-2" />,
            command: () => setShowChangePassword(true),
        },
        { separator: true },
        {
            id: 'logout',
            label: 'Logout',
            icon: <i className="pi pi-sign-out mr-2" />,
            command: () => {
                setIsLoggingOut(true);
                void logout()
                    .then(() => navigate('/login'))
                    .finally(() => setIsLoggingOut(false));
            },
        },
    ];

    const isCallPage = location.pathname.startsWith('/call');

    const {
        incomingCall,
        showIncomingModal,
        isAccepting,
        acceptCall,
        rejectCall,
    } = useIncomingCall();

    const today = formatTodayHeader();

    return (
        <div className="app-wrapper">

            {/* ── ROW 1: White logo bar ───────────────────────────── */}
            <div className="top-bar">
                {/* LEFT: Tieche logo */}
                <div className="top-bar-left">
                    <div className="tieche-logo">
                        <img
                            src={tiecheLogo}
                            alt="Tieche Logo"
                            className="tieche-logo-img"
                        />
                    </div>
                </div>

                {/* CENTER: AvtAR brand */}
                <div className="top-bar-center">
                    <img src={avtARLogo} alt="AvtAR Logo" className="avtar-logo-img" />
                </div>

                {/* RIGHT: Date + user */}
                <div className="top-bar-right">
                    <span className="top-bar-date">{today}</span>
                    <div
                        className="top-bar-user"
                        onClick={(e) => userMenuRef.current?.toggle(e)}
                    >
                        <span>{userName}</span>
                        <i className="pi pi-angle-down" style={{ fontSize: '0.7rem' }} />
                    </div>
                    <Menu model={userMenuItems} popup ref={userMenuRef} />
                </div>
            </div>

            {/* ── Notification-denied banner ───────────────────────── */}
            {notifPermission === 'denied' && showNotifBanner && (
                <div style={{
                    background: '#fffbeb', borderBottom: '1px solid #fde68a',
                    padding: '0.45rem 1.25rem',
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    fontSize: '0.8rem', color: '#92400e',
                }}>
                    <i className="pi pi-bell-slash" style={{ fontSize: '0.9rem', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                        <strong>Notifications blocked.</strong>{' '}
                        You won't receive alerts for incoming calls on other tabs.{' '}
                        To enable, click the{' '}
                        <strong>lock / info icon</strong> in your browser's address bar
                        → <em>Notifications</em> → <strong>Allow</strong>,
                        then reload the page.
                    </span>
                    <button
                        onClick={() => setShowNotifBanner(false)}
                        title="Dismiss"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#92400e', padding: '0.1rem 0.25rem',
                            fontSize: '0.8rem', flexShrink: 0, opacity: 0.7,
                        }}
                    >
                        <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
                    </button>
                </div>
            )}

            {/* ── ROW 2: Teal nav bar ─────────────────────────────── */}
            {!isCallPage && (
                <nav className="nav-bar">
                    {navItems.map(item => (
                        <button
                            key={item.id}
                            className={`nav-item${location.pathname === item.path ? ' nav-item-active' : ''}`}
                            onClick={() => navigate(item.path)}
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>
            )}

            {/* ── Main content ────────────────────────────────────── */}
            <main className="app-main">
                <Outlet />
            </main>

            {/* ── Footer ──────────────────────────────────────────── */}
            <footer className="app-footer">
                <span>Copyright &copy; 2026 TIECHE Inc. All Rights Reserved</span>
                <span>Powered by TIECHE Inc</span>
            </footer>

            <IncomingCallModal
                visible={showIncomingModal}
                incomingCall={incomingCall}
                isAccepting={isAccepting}
                onAccept={acceptCall}
                onReject={rejectCall}
            />

            {showChangePassword && user && (
                <ChangePasswordModal
                    userId={user.id}
                    userEmail={user.email}
                    onClose={() => setShowChangePassword(false)}
                />
            )}

            {isLoggingOut && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(2px)'
                }}>
                    <div className="loading-spinner" style={{ borderTopColor: '#f97316' }} />
                    <div style={{ marginTop: '1rem', color: 'white', fontWeight: 500, fontSize: '1.1rem' }}>Logging out...</div>
                </div>
            )}

        </div>
    );
};

export default AppLayout;
